import React, { useState, useEffect,useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PoolManagementService from './api.js';
import SearchFilter from './components/SearchFilter.js';
import CustomersTab from './tabs/CustomersTab';
import GroupsTab from './tabs/GroupsTab';
import CreateGroupModal from './modals/CreateGroupModal';
import EditGroupModal from './modals/EditGroupModal';
import DeleteGroupModal from './modals/DeleteGroupModal';

import AssignAdvisorModal from './modals/AssignAdvisorModal';
import AssignCustomerModal from './modals/AssignCustomerModal';
import '../../tailwindcss/Tailwind.css';
import { Plus, X } from 'lucide-react';
import TabSelector from './components/TabSelector.js';
import Header from '../userSettings/components/Header.js';
const CustomerGroupManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // UI state
  const [activeTab, setActiveTab] = useState('customers');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');

  // Modal state
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCustomerAssignModal, setShowCustomerAssignModal] = useState(false);
  
  // Selected items
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Form data
  const [groupFormData, setGroupFormData] = useState({
    group_name: '',
    description: ''
  });
  
  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupAdvisors, setGroupAdvisors] = useState({});
  const [loadingAdvisors, setLoadingAdvisors] = useState({});
  
  const [actionLoading, setActionLoading] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [assignFormData, setAssignFormData] = useState({
    group_id: '',
    advisor_id: ''
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [customerAssignFormData, setCustomerAssignFormData] = useState({
    group_id: '',
    customer_id: ''
  });
  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearchTerm(searchTerm);
  }, 500); // 500ms delay
  
  return () => clearTimeout(timer);
}, [searchTerm]);
  // Fetch data on component mount and when filters change
  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    } else if (activeTab === 'groups') {
      fetchGroups();
    }
  }, [activeTab, debouncedSearchTerm, filterGroup]);

  useEffect(() => {
    if (showCustomerAssignModal && customers.length === 0) {
      fetchCustomers();
    }
  }, [showCustomerAssignModal]);

  // API calls
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await PoolManagementService.getCustomers(debouncedSearchTerm, filterGroup);
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to load customers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await PoolManagementService.getGroups(debouncedSearchTerm);
      console.log("Fetched groups:", data);
      setGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to load groups. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupAdvisors = async (groupId) => {
    try {
      setLoadingAdvisors(prev => ({ ...prev, [groupId]: true }));
      const data = await PoolManagementService.getGroupAdvisors(groupId);
      setGroupAdvisors(prev => ({ ...prev, [groupId]: data }));
    } catch (error) {
      console.error(`Error fetching advisors for group ${groupId}:`, error);
      setError('Failed to load advisors. Please try again later.');
    } finally {
      setLoadingAdvisors(prev => ({ ...prev, [groupId]: false }));
    }
  };

    const fetchAdvisors = useCallback(async () => {
    try {
        const loadingKey = 'advisors';
        setLoadingAdvisors(prev => ({ ...prev, [loadingKey]: true }));
        const data = await PoolManagementService.getAdvisors();
        setAdvisors(data);
    } catch (error) {
        console.error('Error fetching advisors:', error);
        setError('Failed to load advisors. Please try again later.');
    } finally {
        setLoadingAdvisors(prev => ({ ...prev, advisors: false }));
    }
    }, []);

  // CRUD operations for groups
  const createGroup = async (formData) => {
    try {
      setActionLoading(true);
      await PoolManagementService.createGroup(formData);
      setShowCreateGroupModal(false);
      resetGroupForm();
      fetchGroups();
      alert('Group created successfully!');
    } catch (error) {
      console.error('Error creating group:', error);
      setError('Failed to create group. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const updateGroup = async () => {
    try {
      setActionLoading(true);
      await PoolManagementService.updateGroup(selectedGroup.id, groupFormData);
      setShowEditGroupModal(false);
      resetGroupForm();
      fetchGroups();
      alert('Group updated successfully!');
    } catch (error) {
      console.error('Error updating group:', error);
      setError('Failed to update group. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteGroup = async () => {
    try {
      setActionLoading(true);
      await PoolManagementService.deleteGroup(selectedGroup.id);
      setShowDeleteGroupModal(false);
      setSelectedGroup(null);
      fetchGroups();
      alert('Group deleted successfully!');
    } catch (error) {
      console.error('Error deleting group:', error);
      setError('Failed to delete group. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  const deleteAdivsorFromGroup = async (groupId, advisorId) => {
    try {
      setActionLoading(true);
      await PoolManagementService.removeAdvisorFromGroup(groupId, advisorId);
      fetchGroupAdvisors(groupId);
      fetchGroups();
      alert('Advisor removed from group successfully!');
    } catch (error) {
      console.error('Error removing advisor from group:', error);
      setError('Failed to remove advisor from group. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  const handleAssignAdvisor = async () => {
    try {
      setActionLoading(true);
      await PoolManagementService.assignAdvisorToGroup(
        assignFormData.group_id,
        assignFormData.advisor_id
      );
      
      if (expandedGroups[assignFormData.group_id]) {
        fetchGroupAdvisors(assignFormData.group_id);
      }
      
      setShowAssignModal(false);
      resetAssignForm();
      fetchGroups();
      alert('Advisor assigned to group successfully!');
    } catch (error) {
      console.error('Error assigning advisor to group:', error);
      setError('Failed to assign advisor to group. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignCustomer = async () => {
    try {
      setActionLoading(true);
      await PoolManagementService.assignCustomerToGroup(
        customerAssignFormData.group_id,
        customerAssignFormData.customer_id
      );
      
      setShowCustomerAssignModal(false);
      resetCustomerAssignForm();
      fetchGroups();
      if (activeTab === 'customers') {
        fetchCustomers();
      }
      alert('Customer assigned to pool successfully!');
    } catch (error) {
      console.error('Error assigning customer to pool:', error);
      setError('Failed to assign customer to pool. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Form reset functions
  const resetGroupForm = () => {
    setGroupFormData({
      group_name: '',
      description: ''
    });
    setSelectedGroup(null);
    setError('');
  };

  const resetAssignForm = () => {
    setAssignFormData({
      group_id: '',
      advisor_id: ''
    });
  };

  const resetCustomerAssignForm = () => {
    setCustomerAssignFormData({
      group_id: '',
      customer_id: ''
    });
  };

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => {
      const newState = { ...prev, [groupId]: !prev[groupId] };
      if (newState[groupId] && !groupAdvisors[groupId]) {
        fetchGroupAdvisors(groupId);
      }
      return newState;
    });
  };

  // Event handlers
  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setGroupFormData({
      group_name: group.group_name,
      description: group.description || ''
    });
    setShowEditGroupModal(true);
  };

  const handleDeleteGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteGroupModal(true);
  };

  if (loading && customers.length === 0 && groups.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  const renderActionButton = () => {
    if (activeTab === 'groups') {
      return (
        <button
          onClick={() => setShowCreateGroupModal(true)}
          className="bg-primary hover:primaryDark text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Pool
        </button>
      );
    }
    return null;
  };
  return (
    <div className="p-4 sm:p-6 min-h-screen min-h-[100dvh]">
      {/* Header with navigation */}
       <Header 
        heading="Customer & Pool Management"
        subHeading="Manage your customers and pools efficiently. Create, edit, and organize pools for better customer management."
        navigate={navigate}
        actionButton={renderActionButton()}
        tabSelector={
          <TabSelector 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
          />
        }
      />

      {/* Error Message */}
      {error && (
        <div className="bg-errorLight border border-error text-errorDark px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => setError('')}
            className="float-right text-error hover:text-errorDark"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <SearchFilter 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterGroup={filterGroup}
        setFilterGroup={setFilterGroup}
        groups={groups}
        activeTab={activeTab}
      />

      {/* Tab Content */}
      {activeTab === 'customers' ? (
        <CustomersTab 
          customers={customers} 
          loading={loading} 
        />
      ) : (
        <GroupsTab 
          groups={groups}
          loading={loading}
          expandedGroups={expandedGroups}
          toggleGroupExpansion={toggleGroupExpansion}
          groupAdvisors={groupAdvisors}
          loadingAdvisors={loadingAdvisors}
          handleEditGroup={handleEditGroup}
          handleDeleteGroup={handleDeleteGroup}
          deleteAdivsorFromGroup={deleteAdivsorFromGroup}
          setAssignFormData={setAssignFormData}
          setShowAssignModal={setShowAssignModal}
          setShowCustomerAssignModal={setShowCustomerAssignModal}
          setCustomerAssignFormData={setCustomerAssignFormData}
        />
      )}

      {/* Modals */}
      {showCreateGroupModal && (
        <CreateGroupModal
          onClose={() => {
            setShowCreateGroupModal(false);
            resetGroupForm();
          }}
          onSubmit={createGroup}
          formData={groupFormData}
          setFormData={setGroupFormData}
          actionLoading={actionLoading}
        />
      )}

      {showEditGroupModal && selectedGroup && (
        <EditGroupModal
          onClose={() => {
            setShowEditGroupModal(false);
            resetGroupForm();
          }}
          onSubmit={updateGroup}
          formData={groupFormData}
          setFormData={setGroupFormData}
          actionLoading={actionLoading}
        />
      )}

      {showDeleteGroupModal && selectedGroup && (
        <DeleteGroupModal
          onClose={() => {
            setShowDeleteGroupModal(false);
            setSelectedGroup(null);
          }}
          onDelete={deleteGroup}
          group={selectedGroup}
          actionLoading={actionLoading}
        />
      )}

      {showAssignModal && (
        <AssignAdvisorModal
          onClose={() => {
            setShowAssignModal(false);
            resetAssignForm();
          }}
          onSubmit={handleAssignAdvisor}
          formData={assignFormData}
          setFormData={setAssignFormData}
          groups={groups}
          advisors={advisors}
          loadingAdvisors={loadingAdvisors.advisors}
          actionLoading={actionLoading}
          fetchAdvisors={fetchAdvisors}
        />
      )}

      {showCustomerAssignModal && (
        <AssignCustomerModal
          onClose={() => {
            setShowCustomerAssignModal(false);
            resetCustomerAssignForm();
          }}
          onSubmit={handleAssignCustomer}
          formData={customerAssignFormData}
          setFormData={setCustomerAssignFormData}
          groups={groups}
          customers={customers}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
};

export default CustomerGroupManagement;